import { BrowserModule } from '@angular/platform-browser';
import { ErrorHandler, Injector, NgModule } from '@angular/core';
import { LOCALE_ID } from '@angular/core';
import { HttpClientModule, HttpClient } from '@angular/common/http';
import { CurrencyPipe, registerLocaleData } from '@angular/common';
import localeEs from '@angular/common/locales/es';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { StoreModule } from '@ngrx/store';

import { AuthGuard } from './shared/services/b2b/auth.guard';
import { SoledadAuthenticationService } from './shared/services/b2b/auth.service';
import { CacheService } from './shared/services/utils/cache.service';
import { EventsService } from './shared/services/utils/events.service';
import { SalesforceService } from './shared/services/utils/salesforce.service';

import { LayoutsModule } from './shared/layouts/layouts.module';
import { LibModule } from './shared/lib/lib.module';
import { SharedModule } from './shared/shared.module';

import { AppComponent } from './app.component';
import { appRouting, appRoutingProviders } from './app.routing';
import { PageNotFoundComponent } from './404/404.component';
import { ProductInfoPrintComponent } from './product-info-print/product-info-print.component';

import * as Reducers from './store/index';
import { env } from '../../env';
import { StyleGuideComponent } from './style-guide/style-guide.component';
import { EffectsModule } from '@ngrx/effects';

import { StoreRouterConnectingModule } from '@ngrx/router-store';
import { AccessModule } from './access/access.module';

import { UtilsService } from './shared/services/api/utils.service';

import { ApiService } from './shared/services/api/api.service';
import { SoledadBannerService } from './shared/services/b2b/banner.service';
import { SoledadUserService } from './shared/services/b2b/user.service';
import { SoledadContactService } from './shared/services/b2b/contact.service';
import { SoledadCheckoutService } from './shared/services/b2b/checkout.service';
import { SoledadCartService } from './shared/services/b2b/cart.service';
import { SoledadDashboardService } from './shared/services/b2b/dashboard.service';
import { SoledadPromosService } from './shared/services/b2b/promos.service';
import { SoledadGiftsService } from './shared/services/b2b/gifts.service';
import { SoledadOpinionsService } from './shared/services/b2b/opinions.service';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { TranslateCompiler, TranslateModule, TranslateLoader, TranslateService } from '@ngx-translate/core';
import { TranslateMessageFormatCompiler } from 'ngx-translate-messageformat-compiler';
import { ZohoService } from './shared/services/b2b/zoho.service';
import { ZonaInfoService } from './shared/services/b2b/zonainfo.service';
import { WebtrekkService } from './shared/services/b2b/webtrekk.service';
import { ConnectifService } from './shared/services/b2b/connectif.service';

import { ToastUtilsService } from './shared/services/api/toast-utils.service';
import { SEOService } from './shared/services/b2b/seo.service';

import { MESSAGE_FORMAT_CONFIG } from 'ngx-translate-messageformat-compiler';

// NgbModule
import { SoledadSaldoService } from './shared/services/b2b/saldo.service';
import { ModalsService } from './shared/services/utils/modals.service';
import { NgMultiSelectDropDownModule } from 'ng-multiselect-dropdown';
import { SoledadMessagesService } from './shared/services/b2b/messages.service';
import { SoledadProductService } from './shared/services/b2b/product.service';
import { SoledadSearchSettingsService } from './shared/services/b2b/searchSettings.service';
import { LanguageService } from './shared/services/utils/language.service';
import { CustomErrorHandler } from './shared/lib/error-handler/customErrorHandler';
import { Router } from '@angular/router';
import { IncidentsService } from './shared/services/b2b/incidents.service';
import { SignupService } from './shared/services/b2b/signup.service';
import { ProvincesService } from './shared/services/b2b/provinces.service';

// Función exportada para traducción
export function HttpLoaderFactory(http: HttpClient) {
    return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

@NgModule({
    declarations: [
        AppComponent,
        PageNotFoundComponent,
        ProductInfoPrintComponent,
        StyleGuideComponent
        // ProductsHighlightComponent,
        // ProductsExtendedComponent
    ],
    imports: [
        LibModule,
        BrowserModule,
        HttpClientModule,
        appRouting,
        SharedModule.forRoot(),
        StoreModule.forRoot(Reducers.reducers),
        EffectsModule.forRoot(Reducers.effects),
        StoreRouterConnectingModule.forRoot({
            stateKey: 'router'
        }),
        StoreDevtoolsModule.instrument({
            maxAge: 25, // Retains last 25 states
            logOnly: env.production // Restrict extension to log-only mode
        }),
        LayoutsModule,
        AccessModule,
        NgMultiSelectDropDownModule.forRoot(),
        // NgbModule.forRoot()
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpClient]
            },
            compiler: {
                provide: TranslateCompiler,
                useClass: TranslateMessageFormatCompiler
            }
        })
    ],
    providers: [
        ApiService,
        appRoutingProviders,
        AuthGuard,
        SoledadBannerService,
        SoledadAuthenticationService,
        CacheService,
        EventsService,
        TranslateService,
        UtilsService,
        ToastUtilsService,
        SoledadUserService,
        SoledadSaldoService,
        SoledadBannerService,
        SoledadMessagesService,
        SoledadProductService,
        SoledadSearchSettingsService,
        TranslateService,
        CurrencyPipe,
        SoledadDashboardService,
        SoledadContactService,
        SoledadCheckoutService,
        SoledadCartService,
        SoledadPromosService,
        SoledadGiftsService,
        SoledadOpinionsService,
        ZohoService,
        IncidentsService,
        SignupService,
        ProvincesService,
        WebtrekkService,
        ConnectifService,
        ZonaInfoService,
        SEOService,
        ModalsService,
        LanguageService,
        SalesforceService,
        {
            provide: LOCALE_ID,
            useValue: 'es-ES'
        },
        { provide: MESSAGE_FORMAT_CONFIG, useValue: { locales: ['pt', 'es', 'en'] } },
        { provide: ErrorHandler, useClass: CustomErrorHandler, deps: [Injector, EventsService] }
    ],
    bootstrap: [AppComponent]
})
export class AppModule {}

registerLocaleData(localeEs, 'es');
